import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart3,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Receipt,
  Loader2,
  FileBarChart,
  Download,
  ExternalLink,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { DashboardLayout } from '../components/DashboardLayout';
import { OverallReportingPage } from './OverallReportingPage';
import { useEntityStore, usePortfolioPrefsStore } from '../store';
import { reportsAPI, portfolioAPI } from '../lib/api';
import { computeDerivedMetrics } from '../lib/portfolioDerived';
import { formatDelta, buildPortfolioMonthRows } from '../lib/reportingUtils';
import { convertAmount, getConversionRate } from '../lib/currencyConvert';
import { CurrencyToggle } from '../components/CurrencyToggle';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';
import { cn } from '../lib/utils';

const CURRENCY_SYMBOLS = { GBP: '£', EUR: '€', USD: '$', DKK: 'kr' };

const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const formatAmount = (value, currency = 'GBP') => {
  const sym = CURRENCY_SYMBOLS[currency] || currency + ' ';
  return `${sym}${Math.abs(value).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

// Signed variant for the portfolio month rows: a loss month (January net
// -3,168) used to print as a positive 3,168 (Fan, 2026-10-25).
const formatSigned = (value, currency = 'GBP') => `${value < 0 ? '-' : ''}${formatAmount(value, currency)}`;

const formatAmountPdf = (value, currency = 'GBP') => {
  const sym = CURRENCY_SYMBOLS[currency] || currency + ' ';
  const sign = value < 0 ? '-' : '';
  return `${sign}${sym}${Math.abs(value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatPct = (v) => v != null ? `${v.toFixed(1)}%` : 'N/A';

const categoryLabels = {
  revenue: 'Revenue',
  supplier_payment: 'Supplier Payments',
  intercompany: 'Intercompany',
  tax_payment: 'Tax Payments',
  subscription: 'Subscriptions',
  personal: 'Personal',
  loan: 'Loans',
  airbnb: 'Airbnb',
  other: 'Other',
};

const categoryColors = {
  revenue: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  supplier_payment: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  tax_payment: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  subscription: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-400',
  intercompany: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  personal: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-400',
  loan: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  airbnb: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
  other: 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-400',
};

function SummaryCard({ label, value, icon: Icon, color, sub }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-3">
          <div className={cn('p-2 rounded-lg', color)}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className={cn('text-2xl font-bold font-mono', sub)}>{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Entity-mode month row (unchanged) ──
function MonthRow({ month, maxRevenue, year }) {
  const [expanded, setExpanded] = useState(false);
  const currentMonth = new Date().getMonth() + 1;
  const hasData = month.transaction_count > 0 || month.paid_invoice_count > 0;
  const isCurrentMonth = month.month === currentMonth;
  const profitPositive = month.net_profit >= 0;

  const revenueWidth = maxRevenue > 0 ? (month.revenue / maxRevenue) * 100 : 0;
  const expensesWidth = maxRevenue > 0 ? (month.expenses / maxRevenue) * 100 : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        'border rounded-xl overflow-hidden transition-shadow hover:shadow-md',
        isCurrentMonth && 'ring-2 ring-primary/30'
      )}
    >
      <button
        className="w-full text-left"
        onClick={() => hasData && setExpanded((v) => !v)}
      >
        <div className="grid grid-cols-[140px_1fr_1fr_1fr_1fr_80px_80px_32px] items-center gap-4 px-5 py-4 bg-card">
          <div className="flex items-center gap-2">
            <span className="font-semibold">{month.month_label}</span>
            {isCurrentMonth && (
              <Badge className="text-[10px] px-1.5 py-0 h-4 bg-primary/10 text-primary border-0">
                Now
              </Badge>
            )}
          </div>

          <div>
            <div className="text-xs text-muted-foreground mb-1">Revenue</div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full bg-green-500 rounded-full transition-all"
                  style={{ width: `${revenueWidth}%` }}
                />
              </div>
              <span className="text-sm font-mono font-medium text-green-600 dark:text-green-400 w-20 text-right">
                {hasData ? fmt(month.revenue) : '—'}
              </span>
            </div>
          </div>

          <div>
            <div className="text-xs text-muted-foreground mb-1">Expenses</div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full bg-red-500 rounded-full transition-all"
                  style={{ width: `${expensesWidth}%` }}
                />
              </div>
              <span className="text-sm font-mono font-medium text-red-600 dark:text-red-400 w-20 text-right">
                {hasData ? fmt(month.expenses) : '—'}
              </span>
            </div>
          </div>

          <div>
            <div className="text-xs text-muted-foreground mb-1">Gross P&L</div>
            <span
              className={cn(
                'text-sm font-mono font-semibold',
                hasData
                  ? month.gross_profit >= 0
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-red-600 dark:text-red-400'
                  : 'text-muted-foreground'
              )}
            >
              {hasData
                ? `${month.gross_profit >= 0 ? '+' : '-'}${fmt(month.gross_profit)}`
                : '—'}
            </span>
          </div>

          <div>
            <div className="text-xs text-muted-foreground mb-1">Net Profit</div>
            <span
              className={cn(
                'text-sm font-mono font-semibold',
                hasData
                  ? profitPositive
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-red-600 dark:text-red-400'
                  : 'text-muted-foreground'
              )}
            >
              {hasData
                ? `${profitPositive ? '+' : '-'}${fmt(month.net_profit)}`
                : '—'}
            </span>
          </div>

          <div className="text-center">
            <div className="text-xs text-muted-foreground mb-1">Txns</div>
            <span className="text-sm font-medium">
              {hasData ? month.transaction_count : '—'}
            </span>
          </div>

          <div className="flex items-center justify-center">
            {hasData && (
              <Link
                to={`/reporting/${year}/${month.month}`}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline font-medium"
              >
                View
                <ExternalLink className="h-3 w-3" />
              </Link>
            )}
          </div>

          <div className="flex items-center justify-center">
            {hasData && (
              <ChevronDown
                className={cn(
                  'h-4 w-4 text-muted-foreground transition-transform',
                  expanded && 'rotate-180'
                )}
              />
            )}
          </div>
        </div>
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 pt-1 bg-muted/30 border-t">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-3">
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                    Transaction Categories
                  </h4>
                  <div className="space-y-2">
                    {Object.entries(month.categories).map(([cat, amount]) => (
                      <div key={cat} className="flex items-center justify-between">
                        <Badge className={cn('text-xs', categoryColors[cat] || categoryColors.other)}>
                          {categoryLabels[cat] || cat}
                        </Badge>
                        <span className="text-sm font-mono">{fmt(amount)}</span>
                      </div>
                    ))}
                    {Object.keys(month.categories).length === 0 && (
                      <p className="text-sm text-muted-foreground">No transactions</p>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                    Invoice Summary
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Paid invoices</span>
                      <span className="font-medium text-green-600">{month.paid_invoice_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Pending invoices</span>
                      <span className="font-medium text-yellow-600">{month.pending_invoice_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Tax paid</span>
                      <span className="font-mono font-medium text-orange-600">
                        {fmt(month.tax_paid)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ── Delta formatting ──

function DeltaBadge({ current, prior, suffix = '' }) {
  const pct = formatDelta(current, prior);
  if (pct == null) return null;
  const positive = pct >= 0;
  return (
    <span className={cn(
      'text-[11px] font-medium ml-1',
      positive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
    )}>
      {positive ? '+' : ''}{pct.toFixed(0)}%{suffix}
    </span>
  );
}

// ── Per-month PDF generation ──

async function generateMonthPdf(monthIdx, year, derived, priorYearDerived, displayCurrency) {
  const { default: jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const generatedOn = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });

  const PRIMARY = [22, 101, 52];
  const MUTED = [100, 116, 139];
  const PAGE_W = doc.internal.pageSize.getWidth();
  const MARGIN = 14;

  const monthLabel = MONTH_LABELS[monthIdx];
  const mk = `${year}-${String(monthIdx + 1).padStart(2, '0')}`;

  // Current month combined data
  const cm = derived.monthlyCombined[monthIdx];
  const eurGbp = derived.fx.EUR_GBP;
  // Convert a GBP-denominated value to the selected display currency
  const cvGbp = (v) => convertAmount(v, 'GBP', displayCurrency, eurGbp);

  // Aggregate per-property for this month
  const propRows = derived.propertiesYtd.map(p => {
    const pm = p.monthly[monthIdx];
    const toDc = getConversionRate(p.currency, displayCurrency, eurGbp);
    return {
      name: p.name,
      revenue: pm.revenue * toDc,
      nights: pm.nights,
      occupancy: pm.occupancy,
      adr: pm.avg_nightly_rate * toDc,
      costs: (pm.management_fees + pm.operational) * toDc,
      net: pm.net_margin * toDc,
    };
  }).filter(r => r.revenue > 0 || r.nights > 0 || r.costs > 0);
  propRows.sort((a, b) => b.revenue - a.revenue);

  // Totals for this month (converted to display currency)
  const totalGrossRevGbp = cvGbp(cm.gross_revenue_gbp || 0);
  const totalRevGbp = cvGbp(cm.revenue_gbp || 0); // net revenue (gross - OTA)
  const totalNights = cm.nights || 0;
  const totalOccPct = cm.occupancy_pct || 0;
  const totalAdrGbp = cvGbp(cm.avg_nightly_rate_gbp || 0);

  // Total costs and net for the month (sum from per-property)
  let totalCostsGbp = 0;
  let totalBookings = 0;
  for (const p of derived.propertiesYtd) {
    const pm = p.monthly[monthIdx];
    const toDc = getConversionRate(p.currency, displayCurrency, eurGbp);
    totalCostsGbp += (pm.management_fees + pm.operational) * toDc;
    totalBookings += pm.bookings;
  }
  const totalNetGbp = totalRevGbp - totalCostsGbp;

  // Prior month values (for delta)
  // For January (monthIdx=0), use December of the prior-year dataset if available.
  let pmPrior = null;
  const pmSource = monthIdx > 0 ? derived : priorYearDerived;
  const pmIdx = monthIdx > 0 ? monthIdx - 1 : 11;
  if (pmSource) {
    const pmCm = pmSource.monthlyCombined[pmIdx];
    const pmEurGbp = pmSource.fx.EUR_GBP;
    let pmCosts = 0, pmBookings = 0;
    for (const p of pmSource.propertiesYtd) {
      const pm2 = p.monthly[pmIdx];
      const toGbp = p.currency === 'GBP' ? 1 : (p.currency === 'EUR' ? pmEurGbp : 0);
      pmCosts += (pm2.management_fees + pm2.operational) * toGbp;
      pmBookings += pm2.bookings;
    }
    pmPrior = {
      grossRevGbp: pmCm.gross_revenue_gbp || 0,
      revGbp: pmCm.revenue_gbp || 0,
      costs: pmCosts,
      net: (pmCm.revenue_gbp || 0) - pmCosts,
      occPct: pmCm.occupancy_pct || 0,
      adrGbp: pmCm.avg_nightly_rate_gbp || 0,
      nights: pmCm.nights || 0,
      bookings: pmBookings,
    };
  }

  // Prior year same month
  let pyPrior = null;
  if (priorYearDerived) {
    const pyCm = priorYearDerived.monthlyCombined[monthIdx];
    const pyEurGbp = priorYearDerived.fx.EUR_GBP;
    let pyCosts = 0, pyBookings = 0;
    for (const p of priorYearDerived.propertiesYtd) {
      const pm2 = p.monthly[monthIdx];
      const toGbp = p.currency === 'GBP' ? 1 : (p.currency === 'EUR' ? pyEurGbp : 0);
      pyCosts += (pm2.management_fees + pm2.operational) * toGbp;
      pyBookings += pm2.bookings;
    }
    pyPrior = {
      grossRevGbp: pyCm.gross_revenue_gbp || 0,
      revGbp: pyCm.revenue_gbp || 0,
      costs: pyCosts,
      net: (pyCm.revenue_gbp || 0) - pyCosts,
      occPct: pyCm.occupancy_pct || 0,
      adrGbp: pyCm.avg_nightly_rate_gbp || 0,
      nights: pyCm.nights || 0,
      bookings: pyBookings,
    };
  }

  const fmtDeltaPdf = (current, prior) => {
    if (prior == null || prior === 0) return '';
    const pct = ((current - prior) / Math.abs(prior)) * 100;
    return `${pct >= 0 ? '+' : ''}${pct.toFixed(0)}%`;
  };

  // ── Header ──
  doc.setFillColor(...PRIMARY);
  doc.rect(0, 0, PAGE_W, 22, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(`Property Portfolio: ${monthLabel} ${year}`, MARGIN, 13);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated: ${generatedOn}`, PAGE_W - MARGIN, 13, { align: 'right' });

  // ── Summary KPIs ──
  doc.setTextColor(30, 30, 30);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(`Summary KPIs (all values ${displayCurrency} approx)`, MARGIN, 32);

  const kpiHead = [['Metric', 'Value', 'vs Prior Month', 'vs Prior Year']];
  const kpiRows = [
    ['Booking Volume', formatAmountPdf(totalGrossRevGbp, displayCurrency), fmtDeltaPdf(totalGrossRevGbp, pmPrior?.grossRevGbp), fmtDeltaPdf(totalGrossRevGbp, pyPrior?.grossRevGbp)],
    ['Revenue after OTA', formatAmountPdf(totalRevGbp, displayCurrency), fmtDeltaPdf(totalRevGbp, pmPrior?.revGbp), fmtDeltaPdf(totalRevGbp, pyPrior?.revGbp)],
    ['P&L-Eligible Costs', formatAmountPdf(totalCostsGbp, displayCurrency), fmtDeltaPdf(totalCostsGbp, pmPrior?.costs), fmtDeltaPdf(totalCostsGbp, pyPrior?.costs)],
    ['Net Result', formatAmountPdf(totalNetGbp, displayCurrency), fmtDeltaPdf(totalNetGbp, pmPrior?.net), fmtDeltaPdf(totalNetGbp, pyPrior?.net)],
    ['Occupancy', formatPct(totalOccPct), fmtDeltaPdf(totalOccPct, pmPrior?.occPct), fmtDeltaPdf(totalOccPct, pyPrior?.occPct)],
    ['Avg Net Nightly Rate', formatAmountPdf(totalAdrGbp, displayCurrency), fmtDeltaPdf(totalAdrGbp, pmPrior?.adrGbp), fmtDeltaPdf(totalAdrGbp, pyPrior?.adrGbp)],
    ['Nights', String(totalNights), fmtDeltaPdf(totalNights, pmPrior?.nights), fmtDeltaPdf(totalNights, pyPrior?.nights)],
    ['Bookings', String(totalBookings), fmtDeltaPdf(totalBookings, pmPrior?.bookings), fmtDeltaPdf(totalBookings, pyPrior?.bookings)],
  ];

  autoTable(doc, {
    startY: 35,
    margin: { left: MARGIN, right: MARGIN },
    head: kpiHead,
    body: kpiRows,
    headStyles: { fillColor: PRIMARY, textColor: 255, fontStyle: 'bold', fontSize: 9 },
    bodyStyles: { fontSize: 9 },
    columnStyles: { 0: { fontStyle: 'bold' }, 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    theme: 'grid',
    didParseCell: (data) => {
      if (data.section === 'body' && (data.column.index === 3 || data.column.index === 2)) {
        const raw = data.cell.raw;
        if (typeof raw === 'string' && raw.startsWith('-')) {
          data.cell.styles.textColor = [220, 38, 38];
        } else if (typeof raw === 'string' && raw.startsWith('+')) {
          data.cell.styles.textColor = [22, 163, 74];
        }
      }
      // Net Result row colour
      if (data.section === 'body' && data.row.index === 3 && data.column.index === 1) {
        data.cell.styles.textColor = totalNetGbp >= 0 ? [22, 163, 74] : [220, 38, 38];
      }
    },
  });

  // ── Per-property table ──
  if (propRows.length > 0) {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text(`Per-Property Breakdown (${propRows.length} properties)`, MARGIN, doc.lastAutoTable.finalY + 10);

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 13,
      margin: { left: MARGIN, right: MARGIN },
      head: [['Property', 'Revenue after OTA', 'Nights', 'Occupancy', 'Avg Net Nightly Rate', 'Costs', 'Net']],
      body: propRows.map(r => [
        r.name,
        formatAmountPdf(r.revenue, displayCurrency),
        String(r.nights),
        formatPct(r.occupancy),
        r.adr > 0 ? formatAmountPdf(r.adr, displayCurrency) : 'N/A',
        formatAmountPdf(r.costs, displayCurrency),
        formatAmountPdf(r.net, displayCurrency),
      ]),
      headStyles: { fillColor: [51, 65, 85], textColor: 255, fontStyle: 'bold', fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        0: { fontStyle: 'bold' },
        1: { halign: 'right' },
        2: { halign: 'right' },
        3: { halign: 'right' },
        4: { halign: 'right' },
        5: { halign: 'right' },
        6: { halign: 'right' },
      },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 6) {
          const row = propRows[data.row.index];
          if (row) {
            data.cell.styles.textColor = row.net >= 0 ? [22, 163, 74] : [220, 38, 38];
          }
        }
      },
      theme: 'grid',
    });
  }

  // ── By Region: UK / France (this month) ──
  const ukMonth = derived.monthlyByRegion?.uk?.[monthIdx];
  const frMonth = derived.monthlyByRegion?.fr?.[monthIdx];
  if (ukMonth || frMonth) {
    const ukRev = cvGbp(ukMonth?.revenue_gbp || 0);
    const ukGross = cvGbp(ukMonth?.gross_revenue_gbp || 0);
    const ukNights = ukMonth?.nights || 0;
    const ukBookings = ukMonth?.bookings || 0;
    const ukOcc = ukMonth?.occupancy_pct;
    const ukAdr = cvGbp(ukMonth?.avg_nightly_rate_gbp || 0);
    const ukCosts = cvGbp((ukMonth?.management_fees_gbp || 0) + (ukMonth?.operational_gbp || 0));
    const ukNet = ukRev - ukCosts;

    const frRev = cvGbp(frMonth?.revenue_gbp || 0);
    const frGross = cvGbp(frMonth?.gross_revenue_gbp || 0);
    const frNights = frMonth?.nights || 0;
    const frBookings = frMonth?.bookings || 0;
    const frOcc = frMonth?.occupancy_pct;
    const frAdr = cvGbp(frMonth?.avg_nightly_rate_gbp || 0);
    const frCosts = cvGbp((frMonth?.management_fees_gbp || 0) + (frMonth?.operational_gbp || 0));
    const frNet = frRev - frCosts;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text(`By Region: ${monthLabel} (all values ${displayCurrency} approx)`, MARGIN, doc.lastAutoTable.finalY + 10);

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 13,
      margin: { left: MARGIN, right: MARGIN },
      head: [['Metric', 'UK', 'France', 'Total']],
      body: [
        ['Booking Volume', formatAmountPdf(ukGross, displayCurrency), formatAmountPdf(frGross, displayCurrency), formatAmountPdf(ukGross + frGross, displayCurrency)],
        ['Revenue after OTA', formatAmountPdf(ukRev, displayCurrency), formatAmountPdf(frRev, displayCurrency), formatAmountPdf(ukRev + frRev, displayCurrency)],
        ['Costs', formatAmountPdf(ukCosts, displayCurrency), formatAmountPdf(frCosts, displayCurrency), formatAmountPdf(ukCosts + frCosts, displayCurrency)],
        ['Net Result', formatAmountPdf(ukNet, displayCurrency), formatAmountPdf(frNet, displayCurrency), formatAmountPdf(ukNet + frNet, displayCurrency)],
        ['Occupancy', formatPct(ukOcc), formatPct(frOcc), formatPct(totalOccPct)],
        ['Avg Net Nightly Rate', formatAmountPdf(ukAdr, displayCurrency), formatAmountPdf(frAdr, displayCurrency), formatAmountPdf(totalAdrGbp, displayCurrency)],
        ['Nights', String(ukNights), String(frNights), String(ukNights + frNights)],
        ['Bookings', String(ukBookings), String(frBookings), String(ukBookings + frBookings)],
      ],
      headStyles: { fillColor: [51, 65, 85], textColor: 255, fontStyle: 'bold', fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        0: { fontStyle: 'bold' },
        1: { halign: 'right' },
        2: { halign: 'right' },
        3: { halign: 'right' },
      },
      didParseCell: (data) => {
        // Net Result row colour
        if (data.section === 'body' && data.row.index === 3) {
          if (data.column.index === 1) data.cell.styles.textColor = ukNet >= 0 ? [22, 163, 74] : [220, 38, 38];
          if (data.column.index === 2) data.cell.styles.textColor = frNet >= 0 ? [22, 163, 74] : [220, 38, 38];
          if (data.column.index === 3) data.cell.styles.textColor = (ukNet + frNet) >= 0 ? [22, 163, 74] : [220, 38, 38];
        }
      },
      theme: 'grid',
      tableWidth: 200,
    });
  }

  // ── Channel mix ──
  const channels = derived.channelBreakdownByMonth[mk] || [];
  if (channels.length > 0) {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text('Channel Mix', MARGIN, doc.lastAutoTable.finalY + 10);

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 13,
      margin: { left: MARGIN, right: MARGIN },
      head: [['Channel', `Revenue (${displayCurrency})`, 'Bookings', '% of Revenue']],
      body: channels.map(c => [
        c.channel,
        formatAmountPdf(cvGbp(c.revenue), displayCurrency),
        String(c.bookings),
        `${c.pct.toFixed(1)}%`,
      ]),
      headStyles: { fillColor: [51, 65, 85], textColor: 255, fontStyle: 'bold', fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
      theme: 'grid',
      tableWidth: 160,
    });
  }

  // ── YTD By Region: UK / France ──
  const ytd = derived.ytdCombined;
  if (ytd) {
    // Sum per-region nights and bookings from monthlyByRegion (not recomputed, just summed)
    const ukMbr = derived.monthlyByRegion?.uk || [];
    const frMbr = derived.monthlyByRegion?.fr || [];
    const ytdUkNights = ukMbr.reduce((s, m) => s + (m.nights || 0), 0);
    const ytdFrNights = frMbr.reduce((s, m) => s + (m.nights || 0), 0);
    const ytdUkBookings = ukMbr.reduce((s, m) => s + (m.bookings || 0), 0);
    const ytdFrBookings = frMbr.reduce((s, m) => s + (m.bookings || 0), 0);
    const ytdUkCosts = cvGbp(ukMbr.reduce((s, m) => s + (m.management_fees_gbp || 0) + (m.operational_gbp || 0), 0));
    const ytdFrCosts = cvGbp(frMbr.reduce((s, m) => s + (m.management_fees_gbp || 0) + (m.operational_gbp || 0), 0));

    const ytdUkRev = cvGbp(ytd.uk?.revenue_gbp || 0);
    const ytdFrRev = ytd.france?.revenue_eur ? convertAmount(ytd.france.revenue_eur, 'EUR', displayCurrency, eurGbp) : 0;
    const ytdUkGross = cvGbp(ytd.uk?.gross_revenue_gbp || 0);
    const ytdFrGross = ytd.france?.gross_revenue_eur ? convertAmount(ytd.france.gross_revenue_eur, 'EUR', displayCurrency, eurGbp) : 0;
    const ytdUkNet = ytdUkRev - ytdUkCosts;
    const ytdFrNet = ytdFrRev - ytdFrCosts;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text(`Year-to-Date By Region: ${year} (all values ${displayCurrency} approx)`, MARGIN, doc.lastAutoTable.finalY + 10);

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 13,
      margin: { left: MARGIN, right: MARGIN },
      head: [['Metric', 'UK', 'France', 'Total']],
      body: [
        ['Booking Volume', formatAmountPdf(ytdUkGross, displayCurrency), formatAmountPdf(ytdFrGross, displayCurrency), formatAmountPdf(ytdUkGross + ytdFrGross, displayCurrency)],
        ['Revenue after OTA', formatAmountPdf(ytdUkRev, displayCurrency), formatAmountPdf(ytdFrRev, displayCurrency), formatAmountPdf(ytdUkRev + ytdFrRev, displayCurrency)],
        ['Costs', formatAmountPdf(ytdUkCosts, displayCurrency), formatAmountPdf(ytdFrCosts, displayCurrency), formatAmountPdf(ytdUkCosts + ytdFrCosts, displayCurrency)],
        ['Net Result', formatAmountPdf(ytdUkNet, displayCurrency), formatAmountPdf(ytdFrNet, displayCurrency), formatAmountPdf(ytdUkNet + ytdFrNet, displayCurrency)],
        ['Occupancy', formatPct(ytd.uk?.occupancy_pct), formatPct(ytd.france?.occupancy_pct), formatPct(ytd.combined?.occupancy_pct)],
        ['Avg Net Nightly Rate', formatAmountPdf(cvGbp(ytd.uk?.avg_nightly_rate_gbp || 0), displayCurrency), formatAmountPdf(cvGbp(ytd.france?.avg_nightly_rate_gbp || 0), displayCurrency), formatAmountPdf(cvGbp(ytd.combined?.avg_nightly_rate_gbp || 0), displayCurrency)],
        ['Nights', String(ytdUkNights), String(ytdFrNights), String(ytdUkNights + ytdFrNights)],
        ['Bookings', String(ytdUkBookings), String(ytdFrBookings), String(ytdUkBookings + ytdFrBookings)],
      ],
      headStyles: { fillColor: PRIMARY, textColor: 255, fontStyle: 'bold', fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        0: { fontStyle: 'bold' },
        1: { halign: 'right' },
        2: { halign: 'right' },
        3: { halign: 'right' },
      },
      didParseCell: (data) => {
        if (data.section === 'body' && data.row.index === 3) {
          if (data.column.index === 1) data.cell.styles.textColor = ytdUkNet >= 0 ? [22, 163, 74] : [220, 38, 38];
          if (data.column.index === 2) data.cell.styles.textColor = ytdFrNet >= 0 ? [22, 163, 74] : [220, 38, 38];
          if (data.column.index === 3) data.cell.styles.textColor = (ytdUkNet + ytdFrNet) >= 0 ? [22, 163, 74] : [220, 38, 38];
        }
      },
      theme: 'grid',
      tableWidth: 200,
    });
  }

  // ── Footer ──
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(...MUTED);
    doc.setFont('helvetica', 'italic');
    doc.text(
      `${displayCurrency} figures are approximate (converted at today's FX rate). Revenue is booking-only.`,
      MARGIN,
      doc.internal.pageSize.getHeight() - 12
    );
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(
      `Page ${i} of ${pageCount}  ·  Confidential  ·  Property Portfolio`,
      PAGE_W / 2,
      doc.internal.pageSize.getHeight() - 6,
      { align: 'center' }
    );
  }

  doc.save(`portfolio-${year}-${String(monthIdx + 1).padStart(2, '0')}-${monthLabel.toLowerCase()}.pdf`);
}

// ── Entity-mode labels ──

const entityLabelsMap = {
  all: 'All Entities',
  br_ltd: 'BR Ltd',
  eci: 'ECI',
  bi_ltd: 'BI Ltd',
  fan_zhang: 'Fan Zhang',
  fan_zhang_piotr_kacki: 'FZ & PK',
  piotr_kacki: 'Piotr Kacki',
};

export function ReportingPage() {
  const { selectedEntity, selectedScope, entities } = useEntityStore();
  const displayCurrency = usePortfolioPrefsStore((s) => s.displayCurrency);
  const isPortfolio = selectedScope === 'portfolio';
  const [year, setYear] = useState(new Date().getFullYear());
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [generatingMonth, setGeneratingMonth] = useState(null);

  // Portfolio mode: raw datasets and derived metrics
  const [dataset, setDataset] = useState(null);
  const [priorYearDataset, setPriorYearDataset] = useState(null);

  const derived = useMemo(() => dataset ? computeDerivedMetrics(dataset) : null, [dataset]);
  const priorYearDerived = useMemo(() => priorYearDataset ? computeDerivedMetrics(priorYearDataset) : null, [priorYearDataset]);

  // Portfolio-mode monthly rows (GBP-converted combined)
  const portfolioMonths = useMemo(
    () => buildPortfolioMonthRows(derived, priorYearDerived, year),
    [derived, priorYearDerived, year]
  );

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      if (isPortfolio) {
        const [ds, pyDs] = await Promise.all([
          portfolioAPI.getDataset({ year }),
          portfolioAPI.getDataset({ year: year - 1 }).catch(() => null),
        ]);
        setDataset(ds);
        setPriorYearDataset(pyDs);
        setReport(null);
      } else {
        // Entities scope renders the monthly financials report below.
        setDataset(null);
        setPriorYearDataset(null);
        setReport(null);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [year, selectedEntity, isPortfolio]);

  useEffect(() => {
    if (entities.length > 0 || selectedEntity === 'all' || isPortfolio) {
      fetchData();
    }
  }, [fetchData, entities]);

  const handleMonthPdf = useCallback(async (monthIdx) => {
    if (!derived) return;
    setGeneratingMonth(monthIdx);
    try {
      await generateMonthPdf(monthIdx, year, derived, priorYearDerived, displayCurrency);
      toast.success('PDF downloaded');
    } catch (err) {
      console.error(err);
      toast.error(getErrorMessage(err));
    } finally {
      setGeneratingMonth(null);
    }
  }, [derived, priorYearDerived, year, displayCurrency]);

  // ── Entity-mode PDF (preserved from original) ──
  const handleDownloadPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      const { default: jsPDF } = await import('jspdf');
      const { default: autoTable } = await import('jspdf-autotable');

      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const generatedOn = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });

      const PRIMARY = [22, 101, 52];
      const MUTED = [100, 116, 139];
      const PAGE_W = doc.internal.pageSize.getWidth();
      const MARGIN = 14;

      const entityLabel = entityLabelsMap[selectedEntity] || selectedEntity;

      doc.setFillColor(...PRIMARY);
      doc.rect(0, 0, PAGE_W, 22, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(`Financial Report — ${year}`, MARGIN, 13);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`Entity: ${entityLabel}   |   Generated: ${generatedOn}`, PAGE_W - MARGIN, 13, { align: 'right' });

      doc.setTextColor(30, 30, 30);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Annual Summary', MARGIN, 32);

      const s = report.summary;
      const sym = CURRENCY_SYMBOLS[reportCurrency] || reportCurrency + ' ';
      const fmtPdf = (v) => `${sym}${Math.abs(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      const signed = (v) => `${v >= 0 ? '+' : '-'}${fmtPdf(v)}`;

      autoTable(doc, {
        startY: 35,
        margin: { left: MARGIN, right: MARGIN },
        head: [['Total Revenue', 'Total Expenses', 'Gross Profit', 'Tax Paid', 'Net Profit']],
        body: [[
          fmtPdf(s.total_revenue),
          fmtPdf(s.total_expenses),
          signed(s.gross_profit),
          fmtPdf(s.total_tax_paid),
          signed(s.net_profit),
        ]],
        headStyles: { fillColor: PRIMARY, textColor: 255, fontStyle: 'bold', fontSize: 9 },
        bodyStyles: { fontSize: 10, fontStyle: 'bold', halign: 'center' },
        columnStyles: {
          2: { textColor: s.gross_profit >= 0 ? [22, 163, 74] : [220, 38, 38] },
          4: { textColor: s.net_profit >= 0 ? [22, 163, 74] : [220, 38, 38] },
        },
        theme: 'grid',
      });

      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 30, 30);
      doc.text('Monthly Breakdown', MARGIN, doc.lastAutoTable.finalY + 10);

      const monthRows = report.months.map((m) => {
        const hasData = m.transaction_count > 0 || m.paid_invoice_count > 0;
        return [
          m.month_label,
          hasData ? fmtPdf(m.revenue) : '—',
          hasData ? fmtPdf(m.expenses) : '—',
          hasData ? signed(m.gross_profit) : '—',
          hasData ? fmtPdf(m.tax_paid) : '—',
          hasData ? signed(m.net_profit) : '—',
          hasData ? String(m.transaction_count) : '—',
          hasData ? `${m.paid_invoice_count} paid / ${m.pending_invoice_count} pending` : '—',
        ];
      });

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 13,
        margin: { left: MARGIN, right: MARGIN },
        head: [['Month', 'Revenue', 'Expenses', 'Gross P&L', 'Tax Paid', 'Net Profit', 'Txns', 'Invoices']],
        body: monthRows,
        headStyles: { fillColor: [51, 65, 85], textColor: 255, fontStyle: 'bold', fontSize: 8 },
        bodyStyles: { fontSize: 8 },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: { 0: { fontStyle: 'bold' } },
        didParseCell: (data) => {
          if (data.section === 'body') {
            const m = report.months[data.row.index];
            if (!m) return;
            if (data.column.index === 3 && m.gross_profit !== 0) {
              data.cell.styles.textColor = m.gross_profit >= 0 ? [22, 163, 74] : [220, 38, 38];
            }
            if (data.column.index === 5 && m.net_profit !== 0) {
              data.cell.styles.textColor = m.net_profit >= 0 ? [22, 163, 74] : [220, 38, 38];
            }
          }
        },
        theme: 'grid',
      });

      const monthsWithData = report.months.filter((m) => Object.keys(m.categories).length > 0);
      if (monthsWithData.length > 0) {
        doc.addPage();

        doc.setFillColor(...PRIMARY);
        doc.rect(0, 0, PAGE_W, 22, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text(`Transaction Categories — ${year}`, MARGIN, 13);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(`Entity: ${entityLabel}`, PAGE_W - MARGIN, 13, { align: 'right' });

        let cursorY = 30;

        for (const m of monthsWithData) {
          if (cursorY > 170) {
            doc.addPage();
            cursorY = 14;
          }

          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(30, 30, 30);
          doc.text(m.month_label, MARGIN, cursorY);
          cursorY += 3;

          autoTable(doc, {
            startY: cursorY,
            margin: { left: MARGIN, right: MARGIN },
            head: [['Category', 'Amount (GBP equiv.)']],
            body: Object.entries(m.categories).map(([cat, amt]) => [
              categoryLabels[cat] || cat,
              fmtPdf(amt),
            ]),
            headStyles: { fillColor: [71, 85, 105], textColor: 255, fontSize: 8, fontStyle: 'bold' },
            bodyStyles: { fontSize: 8 },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            theme: 'grid',
            tableWidth: 100,
          });

          cursorY = doc.lastAutoTable.finalY + 8;
        }
      }

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(...MUTED);
        doc.setFont('helvetica', 'normal');
        doc.text(
          `Page ${i} of ${pageCount}  ·  Confidential — ${entityLabel}`,
          PAGE_W / 2,
          doc.internal.pageSize.getHeight() - 6,
          { align: 'center' }
        );
      }

      doc.save(`report-${year}-${selectedEntity}.pdf`);
      toast.success('PDF downloaded');
    } catch (err) {
      console.error(err);
      toast.error(getErrorMessage(err));
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const reportCurrency = report?.currency || 'GBP';
  const fmt = (v) => formatAmount(v, reportCurrency);

  const maxRevenue = report
    ? Math.max(...report.months.map((m) => m.revenue), 1)
    : 1;

  const summary = report?.summary;

  // Entities scope: the monthly financials report (P&L by business line +
  // cashflow), built on the same entity P&L as the Dashboard and the P&L
  // page. The old transaction-based monthly view crashed ("fmt is not
  // defined") and disagreed with every other page (Fan, 2026-10-25).
  if (!isPortfolio) {
    return <OverallReportingPage />;
  }

  // ── PORTFOLIO MODE ──
  if (isPortfolio) {
    const eurGbpRate = derived?.fx?.EUR_GBP;
    const cvPage = (v) => convertAmount(v, 'GBP', displayCurrency, eurGbpRate);

    const maxPortfolioRev = portfolioMonths.length > 0
      ? Math.max(...portfolioMonths.map(m => cvPage(m.revenue)), 1)
      : 1;

    const currentMonthNum = new Date().getMonth() + 1;
    const currentYearNum = new Date().getFullYear();

    return (
      <DashboardLayout>
        <div className="flex flex-col gap-8" data-testid="reporting-page">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Monthly Reports</h1>
              <p className="text-muted-foreground mt-1">
                Property portfolio performance by month. Download a PDF for any month.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <CurrencyToggle />
              <div className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-card">
                <button onClick={() => setYear((y) => y - 1)} className="p-1 rounded hover:bg-muted transition-colors">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="font-semibold w-12 text-center">{year}</span>
                <button
                  onClick={() => setYear((y) => y + 1)}
                  disabled={year >= currentYearNum}
                  className="p-1 rounded hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : !derived ? null : (
            <div className="space-y-2">
              {/* Column headers */}
              <div className="grid grid-cols-[130px_1fr_100px_100px_80px_70px_70px_60px_90px] gap-3 px-5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <span>Month</span>
                <span>Revenue after OTA ({displayCurrency})</span>
                <span className="text-right">Costs ({displayCurrency})</span>
                <span className="text-right">Net ({displayCurrency})</span>
                <span className="text-right">Occ %</span>
                <span className="text-right">Avg Net Nightly Rate ({displayCurrency})</span>
                <span className="text-right">Nights</span>
                <span className="text-right">Bkgs</span>
                <span />
              </div>

              {portfolioMonths.map((m) => {
                const isCurrentMonth = m.idx + 1 === currentMonthNum && year === currentYearNum;
                const revDc = cvPage(m.revenue);
                const revWidth = maxPortfolioRev > 0 ? (revDc / maxPortfolioRev) * 100 : 0;

                return (
                  <motion.div
                    key={m.mk}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={cn(
                      'border rounded-xl overflow-hidden transition-shadow hover:shadow-md bg-card',
                      isCurrentMonth && 'ring-2 ring-primary/30'
                    )}
                  >
                    <div className="grid grid-cols-[130px_1fr_100px_100px_80px_70px_70px_60px_90px] items-center gap-3 px-5 py-3">
                      {/* Month label */}
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm">{MONTH_LABELS[m.idx]}</span>
                        {isCurrentMonth && (
                          <Badge className="text-[10px] px-1.5 py-0 h-4 bg-primary/10 text-primary border-0">Now</Badge>
                        )}
                      </div>

                      {/* Revenue bar */}
                      <div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                            <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${revWidth}%` }} />
                          </div>
                          <span className="text-sm font-mono font-medium text-green-600 dark:text-green-400 w-20 text-right">
                            {m.hasData ? formatAmount(revDc, displayCurrency) : '—'}
                          </span>
                        </div>
                        {m.pyRevenue != null && m.pyRevenue > 0 && m.hasData && (
                          <DeltaBadge current={m.revenue} prior={m.pyRevenue} suffix=" YoY" />
                        )}
                      </div>

                      {/* Costs */}
                      <span className="text-sm font-mono text-right text-red-600 dark:text-red-400">
                        {m.costs > 0 ? formatAmount(cvPage(m.costs), displayCurrency) : '—'}
                      </span>

                      {/* Net */}
                      <span className={cn(
                        'text-sm font-mono font-semibold text-right',
                        m.hasData
                          ? m.net >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                          : 'text-muted-foreground'
                      )}>
                        {m.hasData ? formatSigned(cvPage(m.net), displayCurrency) : '—'}
                      </span>

                      {/* Occupancy */}
                      <span className="text-sm font-mono text-right">
                        {m.hasData ? formatPct(m.occupancy) : '—'}
                      </span>

                      {/* ADR */}
                      <span className="text-sm font-mono text-right">
                        {m.hasData && m.adr > 0 ? formatAmount(cvPage(m.adr), displayCurrency) : '—'}
                      </span>

                      {/* Nights */}
                      <span className="text-sm font-mono text-right">
                        {m.hasData ? m.nights : '—'}
                      </span>

                      {/* Bookings */}
                      <span className="text-sm font-mono text-right">
                        {m.hasData ? m.bookings : '—'}
                      </span>

                      {/* Download PDF */}
                      <div className="flex justify-end">
                        {m.hasData && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            disabled={generatingMonth === m.idx}
                            onClick={() => handleMonthPdf(m.idx)}
                          >
                            {generatingMonth === m.idx ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <>
                                <Download className="h-3.5 w-3.5 mr-1" />
                                PDF
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}

              {portfolioMonths.every(m => !m.hasData) && (
                <Card>
                  <CardContent className="py-16 text-center">
                    <FileBarChart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium mb-2">No Data for {year}</h3>
                    <p className="text-muted-foreground">No booking or cost data available for this year.</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </DashboardLayout>
    );
  }

  // ── ENTITY MODE (unchanged) ──
  return (
    <DashboardLayout>
      <div className="flex flex-col gap-8" data-testid="reporting-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Reporting</h1>
            <p className="text-muted-foreground mt-1">Monthly financial performance</p>
          </div>

          <div className="flex items-center gap-3">
          {/* Download PDF */}
          <Button
            variant="outline"
            onClick={handleDownloadPDF}
            disabled={!report || isLoading || isGeneratingPDF}
          >
            {isGeneratingPDF ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Download PDF
          </Button>

          {/* Year selector */}
          <div className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-card">
            <button
              onClick={() => setYear((y) => y - 1)}
              className="p-1 rounded hover:bg-muted transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="font-semibold w-12 text-center">{year}</span>
            <button
              onClick={() => setYear((y) => y + 1)}
              disabled={year >= new Date().getFullYear()}
              className="p-1 rounded hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          </div>
        </div>

        {/* Summary stats */}
        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <SummaryCard
              label="Total Revenue"
              value={fmt(summary.total_revenue)}
              icon={TrendingUp}
              color="bg-green-100 dark:bg-green-900/30"
              sub="text-green-600 dark:text-green-400"
            />
            <SummaryCard
              label="Total Expenses"
              value={fmt(summary.total_expenses)}
              icon={TrendingDown}
              color="bg-red-100 dark:bg-red-900/30"
              sub="text-red-600 dark:text-red-400"
            />
            <SummaryCard
              label="Gross Profit"
              value={`${summary.gross_profit >= 0 ? '+' : '-'}${fmt(summary.gross_profit)}`}
              icon={BarChart3}
              color={summary.gross_profit >= 0 ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-red-100 dark:bg-red-900/30'}
              sub={summary.gross_profit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}
            />
            <SummaryCard
              label="Tax Paid"
              value={fmt(summary.total_tax_paid)}
              icon={Receipt}
              color="bg-orange-100 dark:bg-orange-900/30"
              sub="text-orange-600 dark:text-orange-400"
            />
            <SummaryCard
              label="Net Profit"
              value={`${summary.net_profit >= 0 ? '+' : '-'}${fmt(summary.net_profit)}`}
              icon={DollarSign}
              color={summary.net_profit >= 0 ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-red-100 dark:bg-red-900/30'}
              sub={summary.net_profit >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-red-600 dark:text-red-400'}
            />
          </div>
        )}

        {/* Monthly breakdown */}
        <div className="pt-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : !report ? null : (
            <div className="space-y-3">
              {/* Column headers */}
              <div className="grid grid-cols-[140px_1fr_1fr_1fr_1fr_80px_80px_32px] gap-4 px-5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <span>Month</span>
                <span>Revenue</span>
                <span>Expenses</span>
                <span>Gross P&L</span>
                <span>Net Profit</span>
                <span className="text-center">Txns</span>
                <span />
                <span />
              </div>

              {report.months.map((month) => (
                <MonthRow key={month.month} month={month} maxRevenue={maxRevenue} year={year} />
              ))}

              {report.months.every((m) => m.transaction_count === 0 && m.paid_invoice_count === 0) && (
                <Card>
                  <CardContent className="py-16 text-center">
                    <FileBarChart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium mb-2">No Data for {year}</h3>
                    <p className="text-muted-foreground">
                      Generate sample data from any page to populate your monthly reports.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
